<template>
    <div class="cars-layout">
        <AppHeader />
        <main class="main-content">
            <div class="container">
                <div class="row">
                    <aside class="filter-column">
                        <div class="filter-header" @click="toggleFilters">
                            <h2 class="filter-title">
                                <span class="filter-icon">{{ showFilters ? '−' : '+' }}</span>
                                {{ $t('filter_options') }}
                            </h2>
                            <button 
                                class="reset-btn" 
                                @click.stop="resetFilters" 
                                :disabled="!hasActiveFilters"
                            >
                                {{ $t('reset_all') }}
                            </button>
                        </div>
                        <transition name="slide">
                            <div class="filter-content" v-show="showFilters">
                                <CarFilter @filter="handleFilter" />
                            </div>
                        </transition>
                    </aside>
                    <div class="results-column">
                        <div class="results-header">
                            <h2 class="results-title">
                                {{ $t('available_vehicles') }}
                                <span class="results-count" v-if="!loading">
                                    ({{ filteredCars.length }} {{ $t('results') }})
                                </span>
                            </h2>
                            <div class="sort-options">
                                <select class="sort-select" v-model="sortOption">
                                    <option value="price-asc">{{ $t('sort.price_low_to_high') }}</option>
                                    <option value="price-desc">{{ $t('sort.price_high_to_low') }}</option>
                                    <option value="year-desc">{{ $t('sort.newest_first') }}</option>
                                    <option value="mileage-asc">{{ $t('sort.lowest_mileage') }}</option>
                                </select>
                            </div>
                        </div>
                        <div v-if="loading" class="loading-state">
                            <div class="spinner"></div>
                            <p>{{ $t('loading_vehicles') }}</p>
                        </div>
                        <div v-else-if="error" class="error-state">
                            <div class="error-icon">⚠️</div>
                            <h3>{{ $t('error_title') }}</h3>
                            <p>{{ error }}</p>
                            <button class="retry-btn" @click="fetchCars">{{ $t('try_again') }}</button>
                        </div>
                        <div v-else-if="filteredCars.length === 0" class="empty-state">
                            <div class="empty-icon">🚗</div>
                            <h3>{{ $t('no_vehicles_match') }}</h3>
                            <p>
                                {{ $t('try_adjusting_filters') }}
                                <a @click="resetFilters">{{ $t('reset_all_filters') }}</a>
                            </p>
                        </div>
                        <div v-else class="vehicle-grid">
                            <CarCard 
                                v-for="car in sortedCars" 
                                :key="car.id" 
                                :car="car" 
                                @click="navigateToCar(car.id)" 
                            />
                        </div>
                    </div>
                </div>
            </div>
        </main>

        <AppFooter />
    </div>
</template>

<script setup>
    import { ref, computed, onMounted } from 'vue'
    import { useRouter, useRoute } from 'vue-router'
    import { storeToRefs } from 'pinia'
    import { useCarsStore } from '@/stores/cars'
    import AppHeader from '@/components/AppHeader.vue'
    import AppFooter from '@/components/AppFooter.vue'
    import CarCard from '@/components/CarCard.vue'
    import CarFilter from '@/components/CarFilter.vue'
    
    const router = useRouter()
    const route = useRoute()
    const carsStore = useCarsStore()
    const { fetchCars, resetFilters } = carsStore
    const { filteredCars, loading, error, filters } = storeToRefs(carsStore)
    const sortOption = ref('price-asc')
    const showFilters = ref(true)

    const toggleFilters = () => {
        showFilters.value = !showFilters.value
    }

    const sortedCars = computed(() => {
        const cars = [...filteredCars.value]
        switch (sortOption.value) {
            case 'price-asc':
                return cars.sort((a, b) => a.price - b.price)
            case 'price-desc':
                return cars.sort((a, b) => b.price - a.price)
            case 'year-desc':
                return cars.sort((a, b) => b.year - a.year)
            case 'mileage-asc':
                return cars.sort((a, b) => a.mileage - b.mileage)
            default:
                return cars
        }
    })

    const hasActiveFilters = computed(() => {
        return Object.values(filters.value).some(
            value => value !== '' && value !== null
        )
    })

    const navigateToCar = (id) => {
        router.push({
            name: 'car-detail',
            params: { 
                lang: route.params.lang || 'nl',
                id 
            }
        })
        window.scrollTo(0, 0);
    }

    onMounted(() => {
        fetchCars()
        
        showFilters.value = window.innerWidth > 768
        window.addEventListener('resize', () => {
            showFilters.value = window.innerWidth > 768
        })
    })
</script>

<style scoped>
    .cars-layout {
        display: flex;
        flex-direction: column;
        min-height: 100vh;
        background: white;
    }

    .main-content {
        flex: 1;
        padding: 10rem 0;
    }

    .row {
        display: flex;
        gap: 2rem;
    }

    .filter-column {
        width: 280px;
        flex-shrink: 0;
    }

    .filter-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 1.5rem;
        cursor: pointer;
        padding: 0.5rem;
        background-color: #f8f9fa;
        border-radius: 4px;
    }

    .filter-title {
        font-size: 1.25rem;
        margin: 0;
        display: flex;
        align-items: center;
        gap: 0.5rem;
    }

    .filter-icon {
        display: inline-block;
        width: 20px;
        text-align: center;
    }

    .filter-content {
        overflow: hidden;
    }

    .reset-btn {
        background: none;
        border: none;
        color: #45ABFF;
        font-weight: 500;
        cursor: pointer;
        padding: 0.25rem 0.5rem;
    }

    .reset-btn:hover {
        text-decoration: underline;
    }

    .reset-btn:disabled {
        color: #ccc;
        cursor: not-allowed;
        text-decoration: none;
    }

    .results-column {
        flex: 1;
    }

    .results-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 1.5rem;
    }

    .results-title {
        font-size: 1.25rem;
        margin: 0;
    }

    .results-count {
        font-size: 1rem;
        color: #666;
        font-weight: normal;
    }

    .sort-select {
        padding: 0.5rem;
        border: 1px solid #ddd;
        border-radius: 4px;
        background-color: white;
    }

    .vehicle-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
        gap: 1.5rem;
    }

    .loading-state,
    .error-state,
    .empty-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 4rem 2rem;
        text-align: center;
        background-color: white;
        border-radius: 8px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    }

    .spinner {
        border: 4px solid rgba(69, 171, 255, 0.2);
        border-top: 4px solid #45ABFF;
        border-radius: 50%;
        width: 40px;
        height: 40px;
        animation: spin 1s linear infinite;
        margin-bottom: 1rem;
    }

    @keyframes spin {
        0% {
            transform: rotate(0deg);
        }

        100% {
            transform: rotate(360deg);
        }
    }

    .error-icon,
    .empty-icon {
        font-size: 2.5rem;
        margin-bottom: 1rem;
    }

    .error-state h3,
    .empty-state h3 {
        color: #141414;
        margin-bottom: 0.5rem;
    }

    .error-state p,
    .empty-state p {
        color: #666;
        margin-bottom: 1.5rem;
    }

    .retry-btn {
        background: #45ABFF;
        color: white;
        border: none;
        padding: 0.5rem 1.5rem;
        border-radius: 4px;
        cursor: pointer;
    }

    .empty-state a {
        color: #45ABFF;
        cursor: pointer;
        text-decoration: underline;
    }

    /* Mobile styles */
    @media (max-width: 768px) {
        .row {
            flex-direction: column;
        }

        .filter-column {
            width: 100%;
            margin-bottom: 1rem;
        }

        .filter-header {
            margin-bottom: 0;
        }

        .results-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 1rem;
        }

        /* Transition animation */
        .slide-enter-active,
        .slide-leave-active {
            transition: all 0.3s ease;
            max-height: 1000px;
            overflow: hidden;
        }

        .slide-enter-from,
        .slide-leave-to {
            max-height: 0;
            opacity: 0;
        }

        .main-content {
            padding: 15rem 0;
        }
    }
</style>